import { IconCode, IconList, IconBook, IconCards, IconInterview, IconBolt, IconMic } from './icons';

/*
 * Mode registry — the single source of truth for the 5 chatbot modes.
 *
 * Each mode is fully declarative so it can be upgraded to a real structured
 * backend later without touching the chat UI. To deepen a mode, either:
 *   (a) change `buildPrompt` (still uses the streaming /chat endpoint), or
 *   (b) add `handler: async (userText, cfg) => {...}` to bypass /chat and
 *       call a dedicated endpoint, returning { content } or a richer payload.
 *
 * `kind`:
 *   'chat'  — streams from /chat with buildPrompt()
 *   'voice' — renders a launcher that starts the existing WebRTC interview
 */

const subjectsLine = (cfg) => {
  const subs = (cfg?.subjects || []).map((s) => s.name).filter(Boolean);
  return subs.length ? `The learner selected these topics: ${subs.join(', ')}.` : '';
};

const firstSubject = (cfg) => (cfg?.subjects || [])[0]?.name || 'General';

export const MODES = {
  dsa: {
    id: 'dsa',
    label: 'DSA',
    short: 'DSA',
    Icon: IconCode,
    accent: '#818cf8',
    kind: 'chat',
    tagline: 'Problems, explanations and solutions',
    greeting: 'Ask me any DSA problem or pick one to solve. I’ll explain the intuition, give clean code, and analyze complexity.',
    placeholder: 'Ask a DSA question, e.g. “Longest subarray with non-negative product”…',
    suggestions: ['Explain sliding window', 'Two Sum in O(n)', 'When to use a heap?'],
    quickActions: ['Explain with example', 'Show dry run', 'Similar problems', 'More efficient solution'],
    buildPrompt: (userText, cfg) =>
      `You are DataVerse.ai, an expert Data Structures & Algorithms tutor. ${subjectsLine(cfg)}
For the request below: explain the intuition step by step, provide clean, commented code in a fenced \`\`\`python code block (unless another language is requested), and ALWAYS end with the Time Complexity and Space Complexity on their own lines. Then offer a dry run or a related follow-up. Be concise and use markdown.

User: ${userText}`,
    context: (cfg) => ({
      topic: firstSubject(cfg),
      topicMeta: 'DSA • Problem solving',
      relatedLabel: 'Related Topics',
      related: ['Sliding Window', 'Prefix Sums', 'Two Pointers'],
      tryNext: [
        { level: 'easy', text: 'Find first negative in every window' },
        { level: 'medium', text: 'Count subarrays with product < K' },
        { level: 'hard', text: 'Longest subarray with equal 0s and 1s' },
      ],
      progressLabel: 'DSA Progress',
      progressSub: 'Problems solved',
      progress: 75,
    }),
  },

  mcq: {
    id: 'mcq',
    label: 'MCQ',
    short: 'MCQ',
    Icon: IconList,
    accent: '#fbbf24',
    kind: 'chat',
    tagline: 'Multiple choice questions',
    greeting: 'Here’s your quiz — pick an option to see if you’re right, use the navigator to move around, and tap Hint / Explain for AI help on any question.',
    placeholder: 'Type a topic to be quizzed on, or “start”…',
    suggestions: ['Quiz me on Arrays', 'Start a mixed quiz', 'Hard questions only'],
    quickActions: ['Next question', 'Explain answer', 'Change topic'],
    buildPrompt: (userText, cfg) =>
      `You are DataVerse.ai in MCQ quiz mode. ${subjectsLine(cfg)}
Ask exactly ONE multiple-choice question at a time on the requested topic, with four options labelled A) B) C) D). Do NOT reveal the answer yet — end by asking the learner to choose. When the learner replies with a letter, state whether it was correct, briefly explain why, then immediately present the next question. Keep everything short and use markdown.

User: ${userText}`,
    context: (cfg) => ({
      topic: firstSubject(cfg),
      topicMeta: 'MCQ • Assessment',
      relatedLabel: 'Related Topics',
      related: ['Complexity Analysis', 'Sorting', 'Hashing'],
      tryNext: [
        { level: 'easy', text: 'Warm-up: 5 quick questions' },
        { level: 'medium', text: 'Timed round: 10 questions' },
        { level: 'hard', text: 'Challenge: tricky edge cases' },
      ],
      progressLabel: 'Quiz Accuracy',
      progressSub: 'Correct answers',
      progress: 68,
    }),
  },

  theory: {
    id: 'theory',
    label: 'Theory',
    short: 'Theory',
    Icon: IconBook,
    accent: '#38bdf8',
    kind: 'chat',
    tagline: 'A tutor that coaches you through questions',
    greeting: 'I pick questions from your plan, coach you through them, and adapt as you answer — ask a doubt anytime.',
    placeholder: 'Ask a doubt, or answer in your own words…',
    suggestions: ['Explain Big-O notation', 'What is a hash table?', 'Recursion vs iteration'],
    quickActions: ['Go deeper', 'Give an example', 'Summarize', 'Quiz me on this'],
    buildPrompt: (userText, cfg) =>
      `You are DataVerse.ai, a patient theory tutor. ${subjectsLine(cfg)}
Explain the concept below clearly: start with the core intuition, then a concrete worked example, then key points as a short bullet list, and any essential formula. Use short paragraphs and markdown. End by asking whether they want to go deeper or see another example.

User: ${userText}`,
    context: (cfg) => ({
      topic: firstSubject(cfg),
      topicMeta: 'Theory • Concepts',
      relatedLabel: 'Related Concepts',
      related: ['Time Complexity', 'Space Complexity', 'Amortized Analysis'],
      tryNext: [
        { level: 'easy', text: 'Foundations refresher' },
        { level: 'medium', text: 'Common pitfalls & gotchas' },
        { level: 'hard', text: 'Proofs & derivations' },
      ],
      progressLabel: 'Concepts Learned',
      progressSub: 'Topics covered',
      progress: 82,
    }),
  },

  flashcards: {
    id: 'flashcards',
    label: 'Flashcards',
    short: 'Flashcards',
    Icon: IconCards,
    accent: '#c084fc',
    kind: 'chat',
    tagline: 'Quick revision with flashcards',
    greeting: 'Let’s drill some flashcards. Name a topic (or say “go”) — I’ll show a question, you recall it, then say “flip” to reveal the answer.',
    placeholder: 'Name a topic to revise, or say “go”…',
    suggestions: ['Flashcards on Trees', 'Revise sorting algorithms', 'Go'],
    quickActions: ['Flip', 'Next card', 'I got it', 'Review again'],
    buildPrompt: (userText, cfg) =>
      `You are DataVerse.ai in Flashcard revision mode. ${subjectsLine(cfg)}
Present ONE concise flashcard at a time: show only the **Question** (bold) and prompt the learner to recall. When they answer or say "flip", reveal a short, memorizable **Answer**, give a one-line note on whether they were right, then present the next card. Keep every answer to 1–3 sentences. Use markdown.

User: ${userText}`,
    context: (cfg) => ({
      topic: firstSubject(cfg),
      topicMeta: 'Flashcards • Revision',
      relatedLabel: 'Related Decks',
      related: ['Trees', 'Graphs', 'Dynamic Programming'],
      tryNext: [
        { level: 'easy', text: '10-card quick review' },
        { level: 'medium', text: 'Weak spots deck' },
        { level: 'hard', text: 'Interview rapid-fire' },
      ],
      progressLabel: 'Cards Mastered',
      progressSub: 'This deck',
      progress: 54,
    }),
  },

  interviewer: {
    id: 'interviewer',
    label: 'AI Interviewer',
    short: 'Interviewer',
    Icon: IconInterview,
    accent: '#f87171',
    kind: 'voice',
    tagline: 'Simulate a real interview experience',
    greeting: 'Ready for a mock interview? Chat with me here, or start a live voice interview for the full experience.',
    placeholder: 'Answer here, or ask for a practice question…',
    suggestions: ['Ask me a warm-up question', 'System design round', 'Behavioral questions'],
    quickActions: ['Give feedback', 'Next question', 'Harder question'],
    buildPrompt: (userText, cfg) =>
      `You are DataVerse.ai acting as "Alex", a friendly but rigorous technical interviewer. ${subjectsLine(cfg)}
Conduct a mock interview: ask ONE realistic question at a time on the selected topics, wait for the candidate's answer, then give brief constructive feedback (what was good, what to improve) and ask the next question. Keep a professional, encouraging tone. Use markdown.

User: ${userText}`,
    context: (cfg) => ({
      topic: firstSubject(cfg),
      topicMeta: 'Interview • Live practice',
      relatedLabel: 'Focus Areas',
      related: ['Problem solving', 'Communication', 'Complexity trade-offs'],
      tryNext: [
        { level: 'easy', text: 'Warm-up behavioral question' },
        { level: 'medium', text: 'Core DSA round' },
        { level: 'hard', text: 'System design deep-dive' },
      ],
      progressLabel: 'Interview Readiness',
      progressSub: 'Mock sessions',
      progress: 60,
    }),
  },

  blitz: {
    id: 'blitz',
    label: 'Blitz Round',
    short: 'Blitz',
    Icon: IconBolt,
    accent: '#fb923c',
    kind: 'blitz',
    tagline: 'Typed one-liners against the clock',
    greeting: 'Blitz round: one-liner questions, 25 seconds each — type the crispest answer you can. Skip banks one for later.',
    placeholder: 'Type your one-line answer…',
    suggestions: [],
    quickActions: [],
    buildPrompt: (userText) => userText,
    context: (cfg) => ({
      topic: firstSubject(cfg),
      topicMeta: 'Blitz • Typed round',
      relatedLabel: 'Related Topics',
      related: [],
      tryNext: [],
      progressLabel: 'Blitz Progress',
      progressSub: 'This round',
      progress: 0,
    }),
  },

  rapidfire: {
    id: 'rapidfire',
    label: 'AI Rapidfire',
    short: 'Rapidfire',
    Icon: IconMic,
    accent: '#f97316',
    kind: 'voice',
    tagline: 'A spoken blitz round against the clock',
    greeting: 'Rapid fire: one-liner questions, about 25 seconds each, answered out loud. Say "skip" to bank one for later.',
    placeholder: 'The AI rapidfire round is voice-only…',
    suggestions: [],
    quickActions: [],
    buildPrompt: (userText) => userText,
    context: (cfg) => ({
      topic: firstSubject(cfg),
      topicMeta: 'AI Rapidfire • Spoken round',
      relatedLabel: 'Related Topics',
      related: [],
      tryNext: [],
      progressLabel: 'Blitz Progress',
      progressSub: 'This round',
      progress: 0,
    }),
  },
};

/* Ordered list — sidebar, home tiles and settings all render from MODE_LIST. */
export const MODE_ORDER = ['dsa', 'mcq', 'theory', 'flashcards', 'blitz', 'interviewer', 'rapidfire'];
export const MODE_LIST = MODE_ORDER.map((id) => MODES[id]);

export default MODES;
